export const FLIGHT_PASSENGER_NAMES_INCOMPLETE_CODE =
  "FLIGHT_PASSENGER_NAMES_INCOMPLETE";

export const FLIGHT_PASSENGER_NAMES_INCOMPLETE_MESSAGE =
  "יש למלא שם פרטי ושם משפחה לכל נוסע לפני תשלום או שליחה לספק";

export const FLIGHT_PASSENGER_NAMES_INCOMPLETE_TOAST_TITLE = "חסרים שמות נוסעים";

export const FLIGHT_BANK_TRANSFER_ERROR_TOAST_TITLE =
  "שגיאה בבקשת העברה בנקאית";

const SLOT_KEYS = ["adults", "kids", "infants"];

function occupancyOnlyPassengersInfo(roomsAndPaxInfo) {
  if (!Array.isArray(roomsAndPaxInfo) || roomsAndPaxInfo.length === 0) {
    return [{ adults: [{ "@seqNum": "1" }], kids: [], infants: [] }];
  }
  let seq = 0;
  return roomsAndPaxInfo.map((room) => {
    const adults = Array.isArray(room?.adults) ? room.adults : [];
    const kids = Array.isArray(room?.kids) ? room.kids : [];
    const infants = Array.isArray(room?.infants) ? room.infants : [];
    return {
      adults: adults.map(() => ({ "@seqNum": String(++seq) })),
      kids: kids.map((kid) => {
        const age = Number(kid?.age);
        return Number.isFinite(age)
          ? { age, "@seqNum": String(++seq) }
          : { "@seqNum": String(++seq) };
      }),
      infants: infants.map(() => ({ "@seqNum": String(++seq) })),
    };
  });
}

function buildPaxSlotsFromSearchRooms(rooms) {
  const list =
    Array.isArray(rooms) && rooms.length > 0
      ? rooms
      : [{ adults: 2, kids: 0, infants: 0 }];
  return list.map((room) => {
    const adults = Math.max(0, Math.floor(Number(room?.adults) || 0));
    const kids = Math.max(0, Math.floor(Number(room?.kids) || 0));
    const infants = Math.max(0, Math.floor(Number(room?.infants) || 0));
    return {
      adults: Array.from({ length: adults }, () => ({
        firstname: null,
        lastname: null,
      })),
      kids: Array.from({ length: kids }, () => ({
        firstname: null,
        lastname: null,
      })),
      infants: Array.from({ length: infants }, () => ({
        firstname: null,
        lastname: null,
      })),
      room: null,
    };
  });
}

function mergeUiPassengersIntoRooms(roomsAndPaxInfo, passengers) {
  const rooms = JSON.parse(JSON.stringify(roomsAndPaxInfo || []));
  const paxArr = Array.isArray(passengers) ? passengers : [];
  let g = 0;
  for (let ri = 0; ri < rooms.length; ri++) {
    const room = rooms[ri];
    for (const slot of SLOT_KEYS) {
      const list = Array.isArray(room?.[slot]) ? room[slot] : [];
      for (let j = 0; j < list.length; j++) {
        const p = paxArr[g++];
        if (!p || !list[j]) continue;
        list[j] = {
          ...list[j],
          firstname: p.firstName ?? p.firstname ?? list[j].firstname ?? "",
          lastname: p.lastName ?? p.lastname ?? list[j].lastname ?? "",
          dateOfBirth: p.dateOfBirth ?? list[j].dateOfBirth ?? "",
          gender: p.gender ?? list[j].gender ?? "",
          passportNumber: p.passportNumber ?? list[j].passportNumber ?? "",
          passportExpiry: p.passportExpiry ?? list[j].passportExpiry ?? "",
          nationality: p.nationality ?? list[j].nationality ?? "",
          email: p.email ?? list[j].email ?? "",
          cell: p.cell ?? p.phone ?? list[j].cell ?? "",
        };
      }
      if (room) room[slot] = list;
    }
  }
  return rooms;
}

export class FlightPassengerNamesIncompleteError extends Error {
  constructor(message = FLIGHT_PASSENGER_NAMES_INCOMPLETE_MESSAGE) {
    super(message);
    this.name = "FlightPassengerNamesIncompleteError";
    this.code = FLIGHT_PASSENGER_NAMES_INCOMPLETE_CODE;
  }
}

export function trimPassengerName(value) {
  return String(value ?? "").trim();
}

function personDetailsNameParts(pax) {
  const name = pax?.PersonDetails?.Name || pax?.Name;
  if (!name || typeof name !== "object") {
    return { firstname: "", lastname: "" };
  }
  return {
    firstname: trimPassengerName(name.GivenName ?? name.givenName),
    lastname: trimPassengerName(name.Surname ?? name.surname),
  };
}

export function passengerNameParts(pax) {
  if (!pax || typeof pax !== "object") {
    return { firstname: "", lastname: "" };
  }
  const firstname = trimPassengerName(
    pax.firstname ?? pax.firstName ?? pax.first_name
  );
  const lastname = trimPassengerName(
    pax.lastname ?? pax.lastName ?? pax.last_name
  );
  if (firstname || lastname) return { firstname, lastname };
  return personDetailsNameParts(pax);
}

export function passengerHasCompleteName(pax) {
  const { firstname, lastname } = passengerNameParts(pax);
  return Boolean(firstname && lastname);
}

export function passengerHasAnyName(pax) {
  const { firstname, lastname } = passengerNameParts(pax);
  return Boolean(firstname || lastname);
}

export function uiPassengersHaveAnyName(passengers) {
  return (Array.isArray(passengers) ? passengers : []).some(passengerHasAnyName);
}

export function uiPassengersHaveCompleteNames(passengers) {
  const list = Array.isArray(passengers) ? passengers : [];
  if (list.length < 1) return false;
  return list.every(passengerHasCompleteName);
}

export function isFlightPassengerNamesIncompleteError(err) {
  return (
    err instanceof FlightPassengerNamesIncompleteError ||
    err?.code === FLIGHT_PASSENGER_NAMES_INCOMPLETE_CODE ||
    err?.name === "FlightPassengerNamesIncompleteError"
  );
}

/**
 * One toast per bank-transfer failure. Names validation must not also emit
 * the generic "שגיאה בבקשת העברה בנקאית" toast.
 */
export function resolveBankTransferFailureToast(
  err,
  { namesIncompleteAlreadyNotified = false } = {}
) {
  if (err == null) return null;
  if (isFlightPassengerNamesIncompleteError(err)) {
    if (namesIncompleteAlreadyNotified) return null;
    return {
      message: FLIGHT_PASSENGER_NAMES_INCOMPLETE_TOAST_TITLE,
      description: FLIGHT_PASSENGER_NAMES_INCOMPLETE_MESSAGE,
    };
  }
  return {
    message: FLIGHT_BANK_TRANSFER_ERROR_TOAST_TITLE,
    description:
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      "שגיאה לא ידועה",
  };
}

export function collectBankTransferFailureToasts(err) {
  const toast = resolveBankTransferFailureToast(err);
  return toast ? [toast] : [];
}

export function roomsHavePassengerNames(roomsAndPaxInfo) {
  if (!Array.isArray(roomsAndPaxInfo)) return false;
  return roomsAndPaxInfo.some((slot) =>
    SLOT_KEYS.some((key) =>
      (Array.isArray(slot?.[key]) ? slot[key] : []).some(passengerHasAnyName)
    )
  );
}

export function iterFlightPassengerSlots(passengersInfo) {
  const rooms = Array.isArray(passengersInfo) ? passengersInfo : [];
  const out = [];
  rooms.forEach((room, roomIndex) => {
    if (!room || typeof room !== "object") return;
    SLOT_KEYS.forEach((slot) => {
      const list = Array.isArray(room[slot]) ? room[slot] : [];
      list.forEach((pax, paxIndex) => {
        out.push({ roomIndex, slot, paxIndex, pax });
      });
    });
  });
  return out;
}

export function countFlightPassengerSlots(passengersInfo) {
  return iterFlightPassengerSlots(passengersInfo).length;
}

export function flightPassengersHaveCompleteNames(passengersInfo) {
  const slots = iterFlightPassengerSlots(passengersInfo);
  if (slots.length < 1) return false;
  return slots.every((row) => passengerHasCompleteName(row.pax));
}

export function summarizeFlightPassengersForTrace({
  orderId = null,
  passengersInfo,
} = {}) {
  const passenger_count = countFlightPassengerSlots(passengersInfo);
  return {
    order_id: orderId != null && String(orderId).trim() !== "" ? String(orderId) : null,
    passenger_count,
    has_complete_passenger_names: flightPassengersHaveCompleteNames(passengersInfo),
  };
}

function firstNonEmptyName(...values) {
  for (const value of values) {
    const trimmed = trimPassengerName(value);
    if (trimmed) return trimmed;
  }
  return "";
}

function passengerSeqNum(pax) {
  const raw = pax && typeof pax === "object" ? pax["@seqNum"] : null;
  if (raw != null && String(raw).trim() !== "") return String(raw).trim();
  return "";
}

function slotSeqNum(pax, fallback) {
  return passengerSeqNum(pax) || String(fallback);
}

/** Merge key: passenger slot (adults|kids|infants) + @seqNum. Not array index. */
export function passengerSlotSeqKey(slot, pax) {
  const seq = passengerSeqNum(pax);
  if (!seq) return null;
  return `${slot}:${seq}`;
}

function mergeSlotListsBySeqNum(existingList, incomingList, nextSeq) {
  const existing = Array.isArray(existingList) ? existingList : [];
  const incoming = Array.isArray(incomingList) ? incomingList : [];
  const incomingBySeq = new Map();
  incoming.forEach((pax) => {
    const seq = passengerSeqNum(pax);
    if (seq && !incomingBySeq.has(seq)) incomingBySeq.set(seq, pax);
  });
  const usedSeq = new Set();
  const out = existing.map((pax) => {
    const seq = passengerSeqNum(pax);
    const match = seq ? incomingBySeq.get(seq) : null;
    if (seq) usedSeq.add(seq);
    return mergePassengerSlotPreserveExisting(
      pax,
      match || null,
      seq || nextSeq()
    );
  });
  incoming.forEach((pax) => {
    const seq = passengerSeqNum(pax);
    if (seq && usedSeq.has(seq)) return;
    if (seq) usedSeq.add(seq);
    out.push(mergePassengerSlotPreserveExisting(null, pax, seq || nextSeq()));
  });
  return out;
}

export function mergePassengerSlotPreserveExisting(existingPax, incomingPax, seqNum) {
  const existing =
    existingPax && typeof existingPax === "object" ? { ...existingPax } : {};
  const incoming =
    incomingPax && typeof incomingPax === "object" ? { ...incomingPax } : {};
  const {
    firstName: _inFirst,
    lastName: _inLast,
    first_name: _inSnakeFirst,
    last_name: _inSnakeLast,
    ...incomingRest
  } = incoming;
  const {
    firstName: _exFirst,
    lastName: _exLast,
    first_name: _exSnakeFirst,
    last_name: _exSnakeLast,
    ...existingRest
  } = existing;
  const incomingParts = passengerNameParts(incomingPax);
  const existingParts = passengerNameParts(existingPax);
  const firstname = firstNonEmptyName(
    incoming.firstname,
    incoming.firstName,
    incoming.first_name,
    incomingParts.firstname,
    existing.firstname,
    existing.firstName,
    existing.first_name,
    existingParts.firstname
  );
  const lastname = firstNonEmptyName(
    incoming.lastname,
    incoming.lastName,
    incoming.last_name,
    incomingParts.lastname,
    existing.lastname,
    existing.lastName,
    existing.last_name,
    existingParts.lastname
  );
  return {
    ...existingRest,
    ...incomingRest,
    firstname,
    lastname,
    "@seqNum": slotSeqNum(incoming, slotSeqNum(existing, seqNum)),
  };
}

export function mergePassengersInfoPreserveExisting(existingInfo, incomingInfo) {
  const existingRooms = Array.isArray(existingInfo) ? existingInfo : [];
  const incomingRooms = Array.isArray(incomingInfo) ? incomingInfo : [];
  if (incomingRooms.length === 0) {
    return existingRooms.map((room) =>
      room && typeof room === "object" ? { ...room } : room
    );
  }
  let seq = 0;
  const roomCount = Math.max(existingRooms.length, incomingRooms.length);
  const merged = [];
  for (let ri = 0; ri < roomCount; ri++) {
    const existingRoom =
      existingRooms[ri] && typeof existingRooms[ri] === "object"
        ? existingRooms[ri]
        : {};
    const incomingRoom =
      incomingRooms[ri] && typeof incomingRooms[ri] === "object"
        ? incomingRooms[ri]
        : {};
    const nextRoom = { ...existingRoom, ...incomingRoom };
    SLOT_KEYS.forEach((slot) => {
      nextRoom[slot] = mergeSlotListsBySeqNum(
        existingRoom[slot],
        incomingRoom[slot],
        () => String(++seq)
      );
    });
    merged.push(nextRoom);
  }
  return merged;
}

export function stampLdsPassengerNameKeys(roomsAndPaxInfo) {
  if (!Array.isArray(roomsAndPaxInfo)) return [];
  let seq = 0;
  return roomsAndPaxInfo.map((room) => {
    if (!room || typeof room !== "object") return room;
    const next = { ...room };
    SLOT_KEYS.forEach((slot) => {
      const list = Array.isArray(room[slot]) ? room[slot] : [];
      next[slot] = list.map((pax) => {
        const merged = mergePassengerSlotPreserveExisting(null, pax, ++seq);
        return merged;
      });
    });
    return next;
  });
}

function searchOccupancyRoomCount(roomInfo) {
  if (Array.isArray(roomInfo?.rooms) && roomInfo.rooms.length > 0) {
    return roomInfo.rooms.length;
  }
  return null;
}

/**
 * Fare-row copies (2ONEWAY outbound+inbound) are pricing rooms, not extra
 * passengers. Keep search occupancy only so first-click validation does not
 * require names on a phantom extra adult.
 */
export function selectOccupancyRoomsForPassengerPayload(
  roomsAndPaxInfo,
  roomInfo,
  passengers
) {
  const rooms = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [];
  const searchCount = searchOccupancyRoomCount(roomInfo);
  if (searchCount != null && rooms.length > searchCount) {
    return rooms.slice(0, searchCount);
  }
  const paxCount = Array.isArray(passengers) ? passengers.length : 0;
  if (paxCount > 0 && rooms.length > 1) {
    const firstCount = countFlightPassengerSlots(rooms.slice(0, 1));
    const allCount = countFlightPassengerSlots(rooms);
    if (firstCount >= paxCount && allCount > paxCount) {
      return rooms.slice(0, 1);
    }
  }
  return rooms;
}

function buildBaseRoomsForFlightSave({ roomsAndPaxInfo, roomInfo, passengers }) {
  const paxArr = Array.isArray(passengers) ? passengers : [];
  if (Array.isArray(roomsAndPaxInfo) && roomsAndPaxInfo.length > 0) {
    const occupancy = selectOccupancyRoomsForPassengerPayload(
      roomsAndPaxInfo,
      roomInfo,
      paxArr
    );
    if (countFlightPassengerSlots(occupancy) >= 1) return occupancy;
  }
  if (Array.isArray(roomInfo?.rooms) && roomInfo.rooms.length > 0) {
    return buildPaxSlotsFromSearchRooms(roomInfo.rooms);
  }
  if (paxArr.length > 0) {
    return buildPaxSlotsFromSearchRooms([
      { adults: paxArr.length, kids: 0, infants: 0 },
    ]);
  }
  return buildPaxSlotsFromSearchRooms(null);
}

export function buildMergedRoomsAndPaxForFlightSave({
  roomsAndPaxInfo,
  roomInfo,
  passengers,
} = {}) {
  const baseRooms = buildBaseRoomsForFlightSave({
    roomsAndPaxInfo,
    roomInfo,
    passengers,
  });
  return mergeUiPassengersIntoRooms(baseRooms, passengers);
}

export function trimExistingPassengersInfoToIncomingOccupancy(
  existingPassengersInfo,
  incomingRooms
) {
  if (!Array.isArray(existingPassengersInfo) || existingPassengersInfo.length === 0) {
    return existingPassengersInfo;
  }
  if (!Array.isArray(incomingRooms) || incomingRooms.length === 0) {
    return existingPassengersInfo;
  }
  if (existingPassengersInfo.length > incomingRooms.length) {
    return existingPassengersInfo.slice(0, incomingRooms.length);
  }
  return existingPassengersInfo;
}

export function clipUnnamedSlotsBeyondFormCount(passengersInfo, formPassengers) {
  if (!uiPassengersHaveCompleteNames(formPassengers)) return passengersInfo;
  const rooms = Array.isArray(passengersInfo) ? passengersInfo : [];
  let remaining = formPassengers.length;
  const out = [];
  for (const room of rooms) {
    if (remaining <= 0) break;
    if (!room || typeof room !== "object") continue;
    const next = { ...room };
    SLOT_KEYS.forEach((slot) => {
      const list = Array.isArray(room[slot]) ? room[slot] : [];
      const kept = [];
      list.forEach((pax) => {
        if (remaining <= 0) return;
        if (!passengerHasCompleteName(pax)) return;
        kept.push(pax);
        remaining -= 1;
      });
      next[slot] = kept;
    });
    out.push(next);
  }
  return out.length > 0 ? out : passengersInfo;
}

export function resolveIncomingPassengersForPayment({
  roomsAndPaxInfo,
  roomInfo,
  passengers,
  passengersStepValid,
  existingPassengersInfo,
} = {}) {
  const mergedRoomsAndPax = buildMergedRoomsAndPaxForFlightSave({
    roomsAndPaxInfo,
    roomInfo,
    passengers,
  });
  const incoming = resolveFlightDraftPassengersPayload({
    mergedRoomsAndPax,
    passengersStepValid:
      Boolean(passengersStepValid) || uiPassengersHaveCompleteNames(passengers),
    passengers,
    existingPassengersInfo: trimExistingPassengersInfoToIncomingOccupancy(
      existingPassengersInfo,
      mergedRoomsAndPax
    ),
  });
  return clipUnnamedSlotsBeyondFormCount(incoming, passengers);
}

export function resolveFlightDraftPassengersPayload({
  mergedRoomsAndPax,
  passengersStepValid,
  passengers,
  existingPassengersInfo,
} = {}) {
  const uiHasNames =
    roomsHavePassengerNames(mergedRoomsAndPax) ||
    uiPassengersHaveAnyName(passengers);
  const shouldKeepIdentity =
    uiHasNames ||
    (Boolean(passengersStepValid) &&
      Array.isArray(passengers) &&
      passengers.length > 0);
  const incoming = shouldKeepIdentity
    ? stampLdsPassengerNameKeys(mergedRoomsAndPax)
    : occupancyOnlyPassengersInfo(mergedRoomsAndPax);
  return mergePassengersInfoPreserveExisting(existingPassengersInfo, incoming);
}

export function projectPassengersInfoForLdsXml(passengersInfo) {
  return mergePassengersInfoPreserveExisting(null, passengersInfo).map((room) => {
    if (!room || typeof room !== "object") return room;
    const next = { ...room };
    SLOT_KEYS.forEach((slot) => {
      next[slot] = (Array.isArray(room[slot]) ? room[slot] : []).map((pax) => {
        const { firstname, lastname } = passengerNameParts(pax);
        return {
          ...(pax && typeof pax === "object" ? pax : {}),
          firstname,
          lastname,
        };
      });
    });
    return next;
  });
}

export function flightPassengersInfoNeedsSync(existingInfo, incomingInfo) {
  const incomingSlots = iterFlightPassengerSlots(incomingInfo);
  if (incomingSlots.length === 0) return false;
  const existingByKey = new Map();
  iterFlightPassengerSlots(existingInfo).forEach((row) => {
    const key = passengerSlotSeqKey(row.slot, row.pax);
    if (key && !existingByKey.has(key)) existingByKey.set(key, row.pax);
  });
  return incomingSlots.some((row) => {
    const incomingParts = passengerNameParts(row.pax);
    if (!incomingParts.firstname && !incomingParts.lastname) return false;
    const key = passengerSlotSeqKey(row.slot, row.pax);
    const existingPax = key ? existingByKey.get(key) : null;
    const existingParts = passengerNameParts(existingPax);
    return (
      incomingParts.firstname !== existingParts.firstname ||
      incomingParts.lastname !== existingParts.lastname
    );
  });
}

export const FLIGHT_PASSENGERS_INFO_ONLY_UPDATE_KEYS = [
  "ourLdsId",
  "deal_type",
  "passengers_info_only",
  "passengers_info",
];

export function buildFlightPassengersInfoOnlyUpdatePayload({
  ldsOrderId,
  passengersInfo,
} = {}) {
  return {
    ourLdsId: ldsOrderId,
    deal_type: "Flights",
    passengers_info_only: true,
    passengers_info: passengersInfo,
  };
}

export function resolveFlightPaymentPersistAction({
  hasExistingOrder,
  priceDiffers,
  passengersNeedSync,
  skipPriceRefresh = false,
} = {}) {
  if (!hasExistingOrder) return "create_draft";
  // Bank transfer only needs the existing dynamic row id. Recreating the draft
  // via /saveDynamicPackage is a card/session path and can toast a 500 while
  // the transfer itself still succeeds.
  if (priceDiffers && !skipPriceRefresh) return "refresh_price_and_passengers";
  if (passengersNeedSync) return "sync_passengers";
  return "reuse_existing";
}

export function resolveExistingLdsOrderIdFromDynamicRow(orderRow) {
  if (!orderRow || typeof orderRow !== "object") return null;
  const candidates = [
    orderRow.ldsorderid,
    orderRow.ldsOrderId,
    orderRow.lds_order_id,
    orderRow.flights?.id,
    orderRow.flights?.ID,
  ];
  for (const value of candidates) {
    if (value != null && String(value).trim() !== "") return String(value).trim();
  }
  return null;
}

export function existingPassengersInfoFromOrderRow(orderRow) {
  if (!orderRow || typeof orderRow !== "object") return null;
  const candidates = [
    orderRow.flights?.passengersinfo,
    orderRow.flights?.passengers_info,
    orderRow.passengersinfo,
    orderRow.passengers_info,
  ];
  for (const value of candidates) {
    if (Array.isArray(value)) return value;
  }
  return null;
}

export function assertFlightPassengersHaveNames(passengersInfo, formPassengers) {
  if (uiPassengersHaveCompleteNames(formPassengers)) {
    const namedSlots = iterFlightPassengerSlots(passengersInfo).filter((row) =>
      passengerHasCompleteName(row.pax)
    );
    if (namedSlots.length >= formPassengers.length) return true;
    throw new FlightPassengerNamesIncompleteError();
  }
  if (!flightPassengersHaveCompleteNames(passengersInfo)) {
    throw new FlightPassengerNamesIncompleteError();
  }
  return true;
}

export function logFlightPassengerSyncTrace(trace) {
  const payload = {
    order_id: trace?.order_id ?? null,
    passenger_count: Number(trace?.passenger_count) || 0,
    has_complete_passenger_names: Boolean(trace?.has_complete_passenger_names),
    persist_action: trace?.persist_action || null,
  };
  if (typeof console !== "undefined" && typeof console.info === "function") {
    console.info("[FLIGHT_PASSENGER_SYNC]", payload);
  }
  return payload;
}
